import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { StackedBarChart } from '../components/StackedBarChart';
import {
  deltaPct,
  featureLabel,
  formatKrw,
  formatNum,
  sourceColor,
  sourceLabel,
} from '../lib/format';

export function Cost() {
  const summary = useQuery({ queryKey: ['admin', 'cost', 'summary'], queryFn: adminApi.costSummary });
  const bySource = useQuery({ queryKey: ['admin', 'cost', 'by-source'], queryFn: adminApi.costBySource });
  const byFeature = useQuery({ queryKey: ['admin', 'cost', 'by-feature'], queryFn: adminApi.costByFeature });
  const topUsers = useQuery({ queryKey: ['admin', 'cost', 'top-users'], queryFn: () => adminApi.costTopUsers(10) });

  const summaryData = summary.data;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-xl font-semibold text-text-primary">비용</h1>
        <p className="text-sm text-text-muted mt-1">
          Claude API / Clova STT 사용량 — usage_events 기반. AWS는 후속 작업.
        </p>
      </header>

      {/* 1. KPI 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi
          label="오늘"
          value={summaryData ? formatKrw(summaryData.today_cost, true) : '—'}
          delta={summaryData ? deltaPct(summaryData.today_cost, summaryData.yesterday_cost) : null}
          sub={summaryData ? `${formatNum(summaryData.today_events)} 호출` : ''}
        />
        <Kpi
          label="어제"
          value={summaryData ? formatKrw(summaryData.yesterday_cost, true) : '—'}
        />
        <Kpi
          label="이번 달"
          value={summaryData ? formatKrw(summaryData.this_month_cost, true) : '—'}
          delta={summaryData ? deltaPct(summaryData.this_month_cost, summaryData.last_month_cost) : null}
          sub={summaryData ? `${formatNum(summaryData.this_month_events)} 호출` : ''}
        />
        <Kpi
          label="지난 달"
          value={summaryData ? formatKrw(summaryData.last_month_cost, true) : '—'}
        />
      </div>

      {/* 2. 30일 시계열 */}
      <div className="card">
        <h2 className="text-sm font-semibold text-text-primary mb-4">최근 30일 (소스별)</h2>
        {bySource.isLoading ? (
          <div className="text-sm text-text-muted py-12 text-center">로딩 중…</div>
        ) : (
          <StackedBarChart points={bySource.data?.items ?? []} />
        )}
      </div>

      {/* 3 + 4. feature breakdown + top users 나란히 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-sm font-semibold text-text-primary mb-4">기능별 (이번달)</h2>
          {byFeature.isLoading ? (
            <div className="text-sm text-text-muted">로딩 중…</div>
          ) : (
            <FeatureBars rows={byFeature.data?.items ?? []} />
          )}
        </div>

        <div className="card">
          <h2 className="text-sm font-semibold text-text-primary mb-4">Top 유저 (이번달)</h2>
          {topUsers.isLoading ? (
            <div className="text-sm text-text-muted">로딩 중…</div>
          ) : topUsers.data?.items.length === 0 ? (
            <div className="text-sm text-text-muted">데이터 없음</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-text-muted">
                <tr>
                  <th className="text-left text-xs font-medium uppercase tracking-wide py-2">유저</th>
                  <th className="text-right text-xs font-medium uppercase tracking-wide py-2">호출</th>
                  <th className="text-right text-xs font-medium uppercase tracking-wide py-2">비용</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.data?.items.map((u) => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="py-2.5">
                      <Link to={`/users/${u.user_id}`} className="text-accent hover:underline">
                        {u.name ?? u.phone_e164}
                      </Link>
                      {u.email && (
                        <div className="text-xs text-text-muted truncate max-w-[200px]">
                          {u.email}
                        </div>
                      )}
                    </td>
                    <td className="text-right text-text-secondary py-2.5">{formatNum(u.events)}</td>
                    <td className="text-right text-text-primary font-medium py-2.5">
                      {formatKrw(u.cost, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  delta,
  sub,
}: {
  label: string;
  value: string;
  delta?: { pct: number; sign: 'up' | 'down' | 'flat' } | null;
  sub?: string;
}) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="text-2xl font-semibold text-text-primary mt-2">{value}</div>
      <div className="mt-1 flex items-baseline gap-2">
        {delta && (
          <span
            className={
              delta.sign === 'up'
                ? 'text-xs text-warning'
                : delta.sign === 'down'
                  ? 'text-xs text-success'
                  : 'text-xs text-text-muted'
            }
          >
            {delta.sign === 'up' ? '▲' : delta.sign === 'down' ? '▼' : '·'} {delta.pct.toFixed(0)}%
          </span>
        )}
        {sub && <span className="text-xs text-text-muted">{sub}</span>}
      </div>
    </div>
  );
}

function FeatureBars({
  rows,
}: {
  rows: Array<{ source: string; feature: string; cost: number; events: number }>;
}) {
  if (rows.length === 0) return <div className="text-sm text-text-muted">데이터 없음</div>;
  const max = Math.max(...rows.map((r) => r.cost), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const pct = (r.cost / max) * 100;
        return (
          <div key={`${r.source}-${r.feature}`} className="text-xs">
            <div className="flex justify-between mb-1">
              <span className="text-text-secondary">
                <span
                  className="inline-block w-2 h-2 rounded-full mr-1.5 align-baseline"
                  style={{ background: sourceColor(r.source) }}
                />
                {sourceLabel(r.source)} · {featureLabel(r.feature)}
              </span>
              <span className="text-text-primary font-medium">{formatKrw(r.cost, true)}</span>
            </div>
            <div className="h-1.5 bg-surface-elev rounded-sm overflow-hidden">
              <div
                className="h-full"
                style={{ width: `${pct}%`, background: sourceColor(r.source) }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
